import { useState } from 'react';
import { TabSwitcher } from './TabSwitcher';
import { Textarea } from './Textarea';
import { cn } from '../utils';

type JsonFormToggleMode = 'form' | 'json';

export interface JsonFormToggleLabels {
  form: string;
  json: string;
  invalidJson: string;
}

const defaultLabels: JsonFormToggleLabels = {
  form: 'Form',
  json: 'JSON',
  invalidJson: 'Invalid JSON — fix it to re-enable the form view.'
};

export interface JsonFormToggleProps {
  /** Current config object — serialized into the JSON view when switching to it. */
  value: unknown;
  /** The form view of the same value. */
  children: React.ReactNode;
  /** Tab and warning texts — pass translated strings in localized apps. */
  labels?: JsonFormToggleLabels;
  /** Rows of the JSON textarea. */
  rows?: number;
  /** Optional hint shown above the JSON textarea. */
  hint?: string;
  className?: string;
  testId?: string;
  /** Called with the parsed object on every valid JSON edit. */
  onChange(next: Record<string, unknown>): void;
}

const parseRecord = (text: string): Record<string, unknown> | null => {
  try {
    const value: unknown = JSON.parse(text);
    return typeof value === 'object' && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

/**
 * Form/JSON view toggle over one config object. The JSON view edits the same value;
 * invalid JSON blocks the form view with a warning until fixed.
 */
export function JsonFormToggle({
  value,
  children,
  labels = defaultLabels,
  rows = 8,
  hint,
  className,
  testId,
  onChange
}: JsonFormToggleProps) {
  const [mode, setMode] = useState<JsonFormToggleMode>('form');
  const [jsonText, setJsonText] = useState('');
  const [isJsonValid, setIsJsonValid] = useState(true);

  const modeTabs: { id: JsonFormToggleMode; label: string }[] = [
    { id: 'form', label: labels.form },
    { id: 'json', label: labels.json }
  ];

  const handleTabChange = (tab: JsonFormToggleMode): void => {
    if (tab === 'json') {
      setJsonText(JSON.stringify(value, null, 2));
      setIsJsonValid(true);
    }
    setMode(tab);
  };

  const handleJsonChange = (text: string): void => {
    setJsonText(text);
    const parsed = parseRecord(text);
    setIsJsonValid(parsed !== null);
    if (parsed !== null) {
      onChange(parsed);
    }
  };

  return (
    <div data-testid={testId} className={cn('flex flex-col gap-sm', className)}>
      {/* Compact, right-aligned — visually distinct from full-width section tabs above it. */}
      <div className='flex justify-end'>
        <TabSwitcher
          className='w-auto'
          items={modeTabs}
          activeTab={mode}
          onTabChange={handleTabChange}
        />
      </div>
      {mode === 'json' || !isJsonValid ? (
        <div className='flex flex-col gap-xs'>
          {!isJsonValid && <p className='text-sm text-warn-text'>{labels.invalidJson}</p>}
          {hint !== undefined && <p className='text-xs text-foreground-muted'>{hint}</p>}
          <Textarea
            size='full'
            rows={rows}
            value={jsonText}
            onChange={event => handleJsonChange(event.target.value)}
          />
        </div>
      ) : (
        children
      )}
    </div>
  );
}

JsonFormToggle.displayName = 'JsonFormToggle';
